<template>
    <div class="animated fadeIn master-page">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <div v-else>
            <!-- Top Card: Header & Filter -->
            <div class="header-filter-card mb-4">
                <div class="header-row d-flex align-items-center justify-content-between mb-3">
                    <h4 class="page-title mb-0">Master Tipe Pinjaman</h4>
                    <button 
                        type="button" 
                        class="btn btn-tambah"
                        @click="tambahTipePinjaman"
                        name="tambahTipePinjamanBtn"
                    >
                        Tambah Tipe Pinjaman
                    </button>
                </div>

                <!-- Filter Controls -->
                <div class="filter-controls-row d-flex flex-wrap align-items-center gap-2">
                    <div class="filter-item">
                        <b-form-input 
                            v-model="input.nama" 
                            name="filterNama"
                            placeholder="Nama tipe pinjaman"
                            class="form-control-custom filter-input"
                            @keyup.enter="onFilter"
                        ></b-form-input>
                    </div>
                    <div class="filter-item">
                        <b-form-select 
                            v-model="input.filterStatus" 
                            :options="statusOptions"
                            class="form-control-custom filter-select"
                            @change="onFilter"
                        ></b-form-select>
                    </div>
                    <button 
                        type="button" 
                        class="btn btn-search-icon" 
                        :disabled="isBusy" 
                        @click="onFilter" 
                        name="searchBtn"
                        title="Cari"
                    > 
                        <i class="fa fa-search"></i>
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-outline-reset" 
                        :disabled="isBusy" 
                        @click="onReset" 
                        name="resetBtn"
                    >
                        Reset
                    </button>
                </div>
            </div>

            <!-- Table Card -->
            <div class="table-card">
                <div class="table-responsive-wrapper">
                    <b-table 
                        hover 
                        show-empty 
                        :busy="isBusy"
                        :items="dataTipePinjaman" 
                        :fields="fields"
                        responsive 
                        class="tp-table mb-0"
                    >
                        <template v-slot:table-busy>
                            <div class="text-center my-4 py-3">
                                <b-spinner class="align-middle text-success mb-2" small></b-spinner>
                                <p class="mb-0 text-muted small">Memuat data tipe pinjaman...</p>
                            </div>
                        </template>

                        <template v-slot:empty>
                            <div class="text-center py-4 text-muted">
                                Tidak ada data tipe pinjaman.
                            </div>
                        </template>

                        <!-- Slot: KODE -->
                        <template v-slot:cell(kode)="row">
                            <span class="cell-kode">{{ getKode(row.item) }}</span>
                        </template>

                        <!-- Slot: TIPE PINJAMAN -->
                        <template v-slot:cell(tipePinjaman)="row">
                            <span class="cell-tipe">{{ getTipePinjaman(row.item) }}</span>
                        </template>

                        <!-- Slot: JENIS BARANG -->
                        <template v-slot:cell(jenisBarang)="row">
                            <span class="cell-jenis-barang">{{ getJenisBarangCount(row.item) }}</span>
                        </template>

                        <!-- Slot: STATUS -->
                        <template v-slot:cell(status)="row">
                            <span class="badge-status badge-aktif" v-if="isStatusAktif(row.item)">Aktif</span>
                            <span class="badge-status badge-nonaktif" v-else>Nonaktif</span>
                        </template>

                        <!-- Slot: AKSI -->
                        <template v-slot:cell(action)="row">
                            <div class="d-flex align-items-center justify-content-center action-btn-group">
                                <button 
                                    type="button" 
                                    class="btn btn-action-ubah mr-2"
                                    @click="editTipePinjaman(row.item)"
                                    name="ubahTipePinjamanBtn"
                                    title="Ubah Data"
                                >
                                    Ubah
                                </button>
                                <button 
                                    type="button" 
                                    class="btn btn-action-hapus"
                                    @click="confirmDelete(row.item)"
                                    name="hapusTipePinjamanBtn"
                                    title="Hapus Data"
                                >
                                    Hapus
                                </button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- Footer Pagination -->
                <div class="table-pagination-row d-flex flex-wrap align-items-center justify-content-between pt-3">
                    <div class="d-flex align-items-center mb-2 mb-md-0">
                        <span class="mr-2 pagination-label">Tampilkan</span>
                        <b-form-select 
                            v-model="perPage" 
                            :options="perPageOptions" 
                            class="form-control-custom select-per-page mr-3"
                            @change="onPerPageChange"
                        ></b-form-select>
                        <span class="pagination-info text-muted">{{ paginationInfo }}</span>
                    </div>
                    <div>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            class="custom-pagination mb-0"
                            prev-text="‹"
                            next-text="›"
                            first-number
                            last-number
                            @input="onPageChange"
                        ></b-pagination>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Konfirmasi Hapus -->
        <ModalConfirm 
            id="modal-confirm-delete" 
            title="Confirm Hapus Tipe Pinjaman" 
            :isSubmitting="isSubmitting" 
            :desc="deleteDesc" 
            @submit="onDeleteSubmit" 
        />
    </div>
</template>

<script>
export default {
    name: 'master-tipe-pinjaman',
    data() {
        return {
            initStatus: 0,
            isBusy: false,
            isSubmitting: false,
            selectedDeleteItem: null,
            deleteDesc: 'Apakah anda yakin untuk menghapus tipe pinjaman ini?',
            input: {
                nama: '',
                filterStatus: true // default sesuai endpoint API: filterStatus=true
            },
            statusOptions: [
                { value: null, text: 'Status' },
                { value: true, text: 'Aktif' },
                { value: false, text: 'Nonaktif' }
            ],
            dataTipePinjaman: [],
            totalRows: 0,
            currentPage: 1,
            perPage: 10,
            perPageOptions: [
                { value: 10, text: '10' },
                { value: 20, text: '20' },
                { value: 50, text: '50' },
                { value: 100, text: '100' }
            ],
            fields: [
                { 
                    key: 'kode', 
                    label: 'KODE', 
                    thClass: 'text-left align-middle', 
                    tdClass: 'text-left align-middle',
                    thStyle: { width: '12%', minWidth: '70px' }
                },
                {
                    key: 'tipePinjaman',
                    label: 'TIPE PINJAMAN',
                    thClass: 'text-left align-middle',
                    tdClass: 'text-left align-middle',
                    thStyle: { width: '40%', minWidth: '160px' }
                },
                {
                    key: 'jenisBarang',
                    label: 'JENIS BARANG',
                    thClass: 'text-center align-middle',
                    tdClass: 'text-center align-middle',
                    thStyle: { width: '14%', minWidth: '100px' }
                },
                {
                    key: 'status',
                    label: 'STATUS',
                    thClass: 'text-center align-middle',
                    tdClass: 'text-center align-middle',
                    thStyle: { width: '14%', minWidth: '90px' }
                },
                {
                    key: 'action',
                    label: 'AKSI',
                    thClass: 'text-center align-middle',
                    tdClass: 'text-center align-middle',
                    thStyle: { width: '20%', minWidth: '150px' }
                }
            ]
        }
    },
    computed: {
        paginationInfo() {
            if (this.totalRows === 0) return 'Menampilkan 0 data'
            let from = (this.currentPage - 1) * this.perPage + 1
            let to = Math.min(this.currentPage * this.perPage, this.totalRows)
            return `Menampilkan ${from} – ${to} dari ${this.totalRows} data`
        }
    },
    methods: {
        //----------------- Inisialisasi -----------------
        init() {
            this.initStatus = 0
            this.fetchData().then(() => {
                this.initStatus = 1
            }).catch(err => {
                console.error('Error init MasterTipePinjaman:', err)
                this.initStatus = 1
            })
        },

        //----------------- Fetch Data dari API -----------------
        fetchData() {
            this.isBusy = true
            let page = this.currentPage - 1 // Spring pageable is 0-indexed
            let size = this.perPage

            let queryParams = [`page=${page}`, `size=${size}`]

            if (this.input.filterStatus !== null && this.input.filterStatus !== '') {
                queryParams.push(`filterStatus=${this.input.filterStatus}`)
            }

            if (this.input.nama && this.input.nama.trim() !== '') {
                queryParams.push(`filterName=${encodeURIComponent(this.input.nama.trim())}`)
            }

            let url = `tipe-pinjaman/?${queryParams.join('&')}`

            return this.$axios.get(url).then(response => {
                let resData = response.data
                if (Array.isArray(resData)) {
                    this.dataTipePinjaman = resData
                    this.totalRows = resData.length
                } else if (resData && Array.isArray(resData.content)) {
                    this.dataTipePinjaman = resData.content
                    this.totalRows = resData.totalElements || resData.content.length
                } else if (resData && resData.data && Array.isArray(resData.data.content)) {
                    this.dataTipePinjaman = resData.data.content
                    this.totalRows = resData.data.totalElements || resData.data.content.length
                } else if (resData && Array.isArray(resData.data)) {
                    this.dataTipePinjaman = resData.data
                    this.totalRows = resData.total != null ? resData.total : resData.data.length
                } else {
                    this.dataTipePinjaman = []
                    this.totalRows = 0
                }
            }).catch(error => {
                console.error('Gagal mengambil data tipe pinjaman:', error)
                // Fallback attempt without trailing slash if 404/401
                let fallbackUrl = `tipe-pinjaman?${queryParams.join('&')}`
                return this.$axios.get(fallbackUrl).then(response => {
                    let resData = response.data
                    if (Array.isArray(resData)) {
                        this.dataTipePinjaman = resData
                        this.totalRows = resData.length
                    } else if (resData && Array.isArray(resData.content)) {
                        this.dataTipePinjaman = resData.content
                        this.totalRows = resData.totalElements || resData.content.length
                    } else if (resData && resData.data && Array.isArray(resData.data.content)) {
                        this.dataTipePinjaman = resData.data.content
                        this.totalRows = resData.data.totalElements || resData.data.content.length
                    } else if (resData && Array.isArray(resData.data)) {
                        this.dataTipePinjaman = resData.data
                        this.totalRows = resData.total != null ? resData.total : resData.data.length
                    }
                }).catch(err => {
                    console.error('Fallback fetch error:', err)
                    this.dataTipePinjaman = []
                    this.totalRows = 0
                })
            }).finally(() => {
                this.isBusy = false
            })
        },

        //----------------- Helper Format Data -----------------
        getKode(item) {
            return item.kode || item.code || item.kodeTipePinjaman || item.id || '-'
        },

        getTipePinjaman(item) {
            return item.nama || item.name || item.tipePinjaman || item.namaTipePinjaman || '-'
        },

        getJenisBarangCount(item) {
            if (item.totalJenisBarang != null) return item.totalJenisBarang
            if (item.jumlahJenisBarang != null) return item.jumlahJenisBarang
            if (item.jenisBarangCount != null) return item.jenisBarangCount
            if (item.countJenisBarang != null) return item.countJenisBarang
            if (Array.isArray(item.jenisBarang)) return item.jenisBarang.length
            return 0
        },

        isStatusAktif(item) {
            if (item.status === true || item.status === 1 || item.status === 'Aktif' || item.status === 'AKTIF' || item.status === 'true') {
                return true
            }
            if (item.isActive === true || item.active === true) {
                return true
            }
            return false
        },

        //----------------- Filter & Pagination Actions -----------------
        onFilter() {
            this.currentPage = 1
            this.fetchData()
        },

        onReset() {
            this.input.nama = ''
            this.input.filterStatus = true
            this.currentPage = 1
            this.fetchData()
        },

        onPerPageChange() {
            this.currentPage = 1
            this.fetchData()
        },

        onPageChange(page) {
            this.currentPage = page
            this.fetchData()
        },

        //----------------- Navigation & Actions -----------------
        tambahTipePinjaman() {
            this.$router.push(`${this.$route.path}/tambah`).catch(() => {
                this.$helper.toastErr(this, 'Halaman Input Tipe Pinjaman belum tersedia.')
            })
        },

        editTipePinjaman(item) {
            let id = item.id || item.kode
            this.$router.push(`${this.$route.path}/${id}/edit`).catch(() => {
                this.$router.push(`${this.$route.path}/${id}`).catch(() => {
                    this.$helper.toastErr(this, 'Halaman Edit Tipe Pinjaman belum tersedia.')
                })
            })
        },

        //----------------- Hapus Tipe Pinjaman -----------------
        confirmDelete(item) {
            this.selectedDeleteItem = item
            let nama = this.getTipePinjaman(item)
            this.deleteDesc = `Apakah anda yakin untuk menghapus tipe pinjaman "${nama}"?`
            this.$bvModal.show('modal-confirm-delete')
        },

        onDeleteSubmit() {
            if (!this.selectedDeleteItem) return
            this.isSubmitting = true
            let id = this.selectedDeleteItem.id || this.selectedDeleteItem.kode

            let payload = new FormData()
            payload.append('id', id)

            this.$axios.post(`tipe-pinjaman/delete?id=${id}`, payload).then(response => {
                this.$helper.toastSucc(this, 'Tipe pinjaman berhasil dihapus!')
                this.$bvModal.hide('modal-confirm-delete')
                this.fetchData()
            }).catch(error => {
                // Fallback jika API delete menggunakan method DELETE atau tanpa params
                this.$axios.delete(`tipe-pinjaman/delete?id=${id}`).then(() => {
                    this.$helper.toastSucc(this, 'Tipe pinjaman berhasil dihapus!')
                    this.$bvModal.hide('modal-confirm-delete')
                    this.fetchData()
                }).catch(err => {
                    console.error('Delete error:', err)
                    this.$helper.parseError(this, error || err)
                })
            }).finally(() => {
                this.isSubmitting = false
            })
        }
    },
    created() {
        this.init()
    }
}
</script>

<style lang="scss" scoped>
.master-page {
    padding-bottom: 30px;
}

/* Header & Filter Card */
.header-filter-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    border: 1px solid #edf2f0;
}

.page-title {
    font-size: 20px;
    font-weight: 700;
    color: #0b3b36;
    letter-spacing: -0.2px;
}

.btn-tambah {
    background-color: #0b3b36;
    color: #ffffff;
    border: 1px solid #0b3b36;
    border-radius: 8px;
    padding: 8px 18px;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s ease;

    &:hover {
        background-color: #082d29;
        border-color: #082d29;
        color: #ffffff;
    }
}

/* Filter Section */
.filter-controls-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.filter-item {
    min-width: 170px;
}

.filter-input {
    min-width: 190px;
}

.filter-select {
    min-width: 150px;
}

.form-control-custom {
    border-radius: 8px;
    border: 1px solid #dcdfe6;
    font-size: 13px;
    height: 38px;
    color: #334155;
    background-color: #ffffff;

    &:focus {
        border-color: #0b3b36;
        box-shadow: 0 0 0 2px rgba(11, 59, 54, 0.12);
    }

    &::placeholder {
        color: #94a3b8;
    }
}

.btn-search-icon {
    background-color: #0b3b36;
    color: #ffffff;
    border: 1px solid #0b3b36;
    border-radius: 8px;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: #082d29;
        border-color: #082d29;
        color: #ffffff;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.btn-outline-reset {
    background-color: #ffffff;
    color: #475569;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    height: 38px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: #f8fafc;
        border-color: #cbd5e1;
        color: #1e293b;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

/* Table Card */
.table-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    border: 1px solid #edf2f0;
}

.table-responsive-wrapper {
    overflow-x: auto;
}

.tp-table {
    width: 100%;

    ::v-deep {
        thead th {
            background-color: #fafcfb;
            color: #5e7a75;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.6px;
            text-transform: uppercase;
            border-top: none;
            border-bottom: 1px solid #eef2f1;
            padding: 14px 16px;
            white-space: nowrap;
        }

        tbody td {
            padding: 14px 16px;
            font-size: 13.5px;
            color: #334155;
            border-top: 1px solid #f1f5f4;
        }

        tbody tr:hover {
            background-color: #fcfdfd;
        }
    }
}

.cell-kode {
    font-weight: 600;
    color: #1e293b;
}

.cell-tipe {
    color: #1e293b;
    font-weight: 500;
}

.cell-jenis-barang {
    color: #1e293b;
    font-weight: 500;
}

/* Badges */
.badge-status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1.2;
}

.badge-aktif {
    background-color: #e8f5e9;
    color: #166534;
    border: 1px solid #bbf7d0;
}

.badge-nonaktif {
    background-color: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

/* Action Buttons */
.action-btn-group {
    display: flex;
    justify-content: center;
    align-items: center;
}

.btn-action-ubah {
    background-color: #ffffff;
    color: #334155;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    padding: 4px 14px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background-color: #f8fafc;
        border-color: #cbd5e1;
        color: #0b3b36;
    }
}

.btn-action-hapus {
    background-color: #ffffff;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 4px 14px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background-color: #fef2f2;
        border-color: #fca5a5;
        color: #b91c1c;
    }
}

/* Pagination */
.table-pagination-row {
    border-top: 1px solid #f1f5f4;
}

.pagination-label {
    font-size: 13px;
    color: #64748b;
}

.select-per-page {
    width: 70px !important;
    height: 34px;
    font-size: 13px;
    padding: 2px 8px;
}

.pagination-info {
    font-size: 13px;
    color: #64748b;
}

.custom-pagination {
    ::v-deep {
        .page-item .page-link {
            border-radius: 6px;
            margin: 0 2px;
            border: 1px solid #e2e8f0;
            color: #475569;
            font-size: 13px;
            min-width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 6px;
        }

        .page-item.active .page-link {
            background-color: #0b3b36;
            border-color: #0b3b36;
            color: #ffffff;
            font-weight: 600;
        }

        .page-item.disabled .page-link {
            color: #cbd5e1;
            background-color: #f8fafc;
            border-color: #e2e8f0;
        }
    }
}

@media (max-width: 768px) {
    .header-row {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 12px;
    }

    .filter-controls-row {
        flex-direction: column;
        align-items: stretch !important;
    }

    .filter-item,
    .filter-input,
    .filter-select {
        width: 100% !important;
        min-width: 100% !important;
    }

    .btn-search-icon,
    .btn-outline-reset {
        width: 100% !important;
    }
}
</style>